import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createB2BClient,
  createB2BInvoice,
  deleteB2BDraft,
  fetchB2BClients,
  fetchB2BInvoices,
  issueB2BInvoice,
  markB2BInvoicePaid,
  openB2BInvoicePDF,
  updateB2BClient,
  updateB2BInvoice,
} from "../api.js";

const TAX_MODES = [
  { value: "EU_B2B_REVERSE_CHARGE", label: "EU B2B Reverse Charge" },
  { value: "DE_KLEINUNTERNEHMER", label: "DE Kleinunternehmer §19" },
  { value: "NON_EU_B2B", label: "Non-EU B2B" },
  { value: "DOMESTIC_STANDARD", label: "Inland Regelbesteuerung" },
  { value: "CUSTOM", label: "Eigener Steuerhinweis" },
];

const STATUS_LABELS = {
  draft: "Entwurf",
  issued: "Ausgestellt",
  paid: "Bezahlt",
  cancelled: "Storniert",
};

const EMPTY_CLIENT = {
  clientCode: "",
  companyName: "",
  legalForm: "",
  street: "",
  postalCode: "",
  city: "",
  countryCode: "AT",
  countryName: "Österreich",
  vatId: "",
  billingEmail: "",
  defaultLanguage: "de",
  defaultCurrency: "EUR",
  paymentTermsDays: 14,
};

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100";

function Field({ label, hint, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      {label ? (
        <span className="mb-1 block text-xs font-semibold text-slate-700">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </span>
      ) : null}
      {children}
      {hint ? (
        <span className="mt-1 block text-[11px] leading-snug text-slate-500">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 first:mt-0">
      {children}
    </div>
  );
}

function emptyInvoiceForm(clientId = "") {
  return {
    clientId: clientId ? String(clientId) : "",
    invoiceDate: "",
    servicePeriodFrom: "",
    servicePeriodTo: "",
    dueDate: "",
    language: "de",
    currency: "EUR",
    taxMode: "EU_B2B_REVERSE_CHARGE",
    vatRate: 0,
    taxNoteOverride: "",
    poNumber: "",
    contractReference: "",
    notes: "",
    items: [{ description: "", qty: 1, unitPrice: 0 }],
  };
}

function money(value) {
  return Number(value || 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function statusBadge(status) {
  const map = {
    draft: "bg-amber-50 text-amber-800 border-amber-200",
    issued: "bg-emerald-50 text-emerald-800 border-emerald-200",
    paid: "bg-blue-50 text-blue-800 border-blue-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return map[status] || map.draft;
}

function addDaysIso(dateStr, days) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

function toDateOnly(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  return '';
}

function formatDisplayDate(value) {
  const iso = toDateOnly(value);
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
}

function invoiceToForm(invoice) {
  const items = Array.isArray(invoice.items)
    ? invoice.items
    : typeof invoice.items === "string"
      ? JSON.parse(invoice.items || "[]")
      : [];

  return {
    clientId: String(invoice.client_id || ""),
    invoiceDate: toDateOnly(invoice.invoice_date),
    servicePeriodFrom: toDateOnly(invoice.service_period_from),
    servicePeriodTo: toDateOnly(invoice.service_period_to),
    dueDate: toDateOnly(invoice.due_date),
    language: invoice.language || "de",
    currency: invoice.currency || "EUR",
    taxMode: invoice.tax_mode || "EU_B2B_REVERSE_CHARGE",
    vatRate: Number(invoice.vat_rate || 0),
    taxNoteOverride: invoice.tax_note_override || "",
    poNumber: invoice.po_number || "",
    contractReference: invoice.contract_reference || "",
    notes: invoice.notes || "",
    items: items.length
      ? items.map((it) => ({
          description: it.description || "",
          qty: Number(it.qty || 1),
          unitPrice: Number(it.unitPrice || 0),
        }))
      : [{ description: "", qty: 1, unitPrice: 0 }],
  };
}

export default function B2BInvoiceAdmin({ isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("invoices");
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);

  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm());

  const loadAll = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setErr("");
    try {
      const [c, i] = await Promise.all([
        fetchB2BClients(),
        fetchB2BInvoices({
          clientId: clientFilter || undefined,
          status: statusFilter || undefined,
        }),
      ]);
      setClients(c.clients || []);
      setInvoices(i.invoices || []);
    } catch (error) {
      setErr(
        error?.status === 403
          ? "Nur die Admin-Wallet hat Zugriff auf B2B-Rechnungen."
          : error?.message || "B2B-Daten konnten nicht geladen werden."
      );
    } finally {
      setLoading(false);
    }
  }, [isAdmin, clientFilter, statusFilter]);

  useEffect(() => {
    if (open) loadAll();
  }, [open, loadAll]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("v3tax:open-b2b-invoices", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("v3tax:open-b2b-invoices", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const activeClients = useMemo(
    () => clients.filter((c) => c.active !== false),
    [clients]
  );

  const selectedClient = useMemo(
    () =>
      clients.find((c) => String(c.id) === String(invoiceForm.clientId)) ||
      null,
    [clients, invoiceForm.clientId]
  );

  const draftNetTotal = useMemo(
    () =>
      invoiceForm.items.reduce(
        (sum, item) =>
          sum + Number(item.qty || 0) * Number(item.unitPrice || 0),
        0
      ),
    [invoiceForm.items]
  );

  const openNewClient = () => {
    setEditingClientId(null);
    setClientForm(EMPTY_CLIENT);
    setShowClientForm(true);
    setShowInvoiceForm(false);
  };

  const openEditClient = (client) => {
    setEditingClientId(client.id);
    setClientForm({
      clientCode: client.client_code || "",
      companyName: client.company_name || "",
      legalForm: client.legal_form || "",
      street: client.street || "",
      postalCode: client.postal_code || "",
      city: client.city || "",
      countryCode: client.country_code || "",
      countryName: client.country_name || "",
      vatId: client.vat_id || "",
      billingEmail: client.billing_email || "",
      defaultLanguage: client.default_language || "de",
      defaultCurrency: client.default_currency || "EUR",
      paymentTermsDays: Number(client.payment_terms_days || 14),
      active: client.active !== false,
    });
    setShowClientForm(true);
    setShowInvoiceForm(false);
    setTab("clients");
  };

  const saveClient = async () => {
    setBusy(true);
    setErr("");
    try {
      if (editingClientId) {
        await updateB2BClient(editingClientId, {
          companyName: clientForm.companyName,
          legalForm: clientForm.legalForm,
          street: clientForm.street,
          postalCode: clientForm.postalCode,
          city: clientForm.city,
          countryCode: clientForm.countryCode,
          countryName: clientForm.countryName,
          vatId: clientForm.vatId,
          billingEmail: clientForm.billingEmail,
          defaultLanguage: clientForm.defaultLanguage,
          defaultCurrency: clientForm.defaultCurrency,
          paymentTermsDays: clientForm.paymentTermsDays,
          active: clientForm.active !== false,
        });
      } else {
        await createB2BClient(clientForm);
      }
      setShowClientForm(false);
      await loadAll();
    } catch (error) {
      setErr(error?.message || "Kunde konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const openNewInvoice = (clientId = "") => {
    const form = emptyInvoiceForm(clientId);
    const client = clients.find((c) => String(c.id) === String(clientId));
    if (client) {
      form.language = client.default_language || "de";
      form.currency = client.default_currency || "EUR";
    }
    setEditingInvoiceId(null);
    setInvoiceForm(form);
    setShowInvoiceForm(true);
    setShowClientForm(false);
    setTab("invoices");
  };

  const openEditInvoice = (invoice) => {
    if (invoice.status !== "draft") return;
    setEditingInvoiceId(invoice.id);
    setInvoiceForm(invoiceToForm(invoice));
    setShowInvoiceForm(true);
    setShowClientForm(false);
    setTab("invoices");
  };

  const onClientChange = (clientId) => {
    const client = clients.find((c) => String(c.id) === String(clientId));
    setInvoiceForm((prev) => {
      const next = {
        ...prev,
        clientId: String(clientId),
        language: client?.default_language || prev.language,
        currency: client?.default_currency || prev.currency,
      };
      if (prev.invoiceDate && client?.payment_terms_days != null) {
        next.dueDate = addDaysIso(prev.invoiceDate, client.payment_terms_days);
      }
      return next;
    });
  };

  const onInvoiceDateChange = (invoiceDate) => {
    setInvoiceForm((prev) => {
      const terms =
        selectedClient?.payment_terms_days ??
        clients.find((c) => String(c.id) === String(prev.clientId))
          ?.payment_terms_days ??
        14;
      return {
        ...prev,
        invoiceDate,
        dueDate: addDaysIso(invoiceDate, terms),
      };
    });
  };

  const updateItem = (index, patch) => {
    setInvoiceForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  const saveInvoice = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = {
        clientId: Number(invoiceForm.clientId),
        invoiceDate: invoiceForm.invoiceDate || null,
        servicePeriodFrom: invoiceForm.servicePeriodFrom || null,
        servicePeriodTo: invoiceForm.servicePeriodTo || null,
        dueDate: invoiceForm.dueDate || null,
        language: invoiceForm.language,
        currency: invoiceForm.currency,
        taxMode: invoiceForm.taxMode,
        vatRate: Number(invoiceForm.vatRate || 0),
        taxNoteOverride: invoiceForm.taxNoteOverride || null,
        items: invoiceForm.items,
        poNumber: invoiceForm.poNumber || null,
        contractReference: invoiceForm.contractReference || null,
        notes: invoiceForm.notes || null,
      };

      if (editingInvoiceId) {
        await updateB2BInvoice(editingInvoiceId, payload);
      } else {
        await createB2BInvoice(payload);
      }

      setShowInvoiceForm(false);
      setEditingInvoiceId(null);
      await loadAll();
    } catch (error) {
      setErr(error?.message || "Rechnung konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (fn) => {
    setBusy(true);
    setErr("");
    try {
      await fn();
      await loadAll();
    } catch (error) {
      setErr(error?.body?.error || error?.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      {open ? (
        <div
          className="fixed inset-0 z-[270] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] sm:p-6"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="flex max-h-[92vh] w-[min(96vw,1180px)] flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_25px_80px_rgba(15,23,42,0.25)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-4">
              <div>
                <div className="text-lg font-bold text-emerald-900">
                  B2B Invoice Center
                </div>
                <div className="mt-0.5 text-sm text-slate-600">
                  Kunden, Entwürfe &amp; Rechnungen — getrennt vom B2C-Flow
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Schließen
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
              <button
                type="button"
                onClick={() => setTab("invoices")}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                  tab === "invoices"
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                Rechnungen
              </button>
              <button
                type="button"
                onClick={() => setTab("clients")}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                  tab === "clients"
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                Kunden
              </button>

              <div className="ml-auto flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openNewClient}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                >
                  + Kunde
                </button>
                <button
                  type="button"
                  onClick={() => openNewInvoice()}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  + Rechnung
                </button>
                <button
                  type="button"
                  onClick={loadAll}
                  disabled={loading || busy}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Aktualisieren
                </button>
              </div>
            </div>

            {err ? (
              <div className="mx-5 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {err}
              </div>
            ) : null}

            <div className="overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  Lade B2B-Daten…
                </div>
              ) : null}

              {!loading && tab === "clients" ? (
                <div className="space-y-3">
                  {clients.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
                      Noch keine B2B-Kunden. Lege z.&nbsp;B. Blockpit als ersten
                      Datensatz an.
                    </div>
                  ) : (
                    clients.map((client) => (
                      <div
                        key={client.id}
                        className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4"
                      >
                        <div>
                          <div className="font-semibold text-slate-900">
                            {client.company_name}
                            {!client.active ? (
                              <span className="ml-2 text-xs font-medium text-slate-400">
                                (inaktiv)
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            Code: {client.client_code} · {client.street},{" "}
                            {client.postal_code} {client.city},{" "}
                            {client.country_name}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            USt-ID: {client.vat_id || "—"} · Sprache:{" "}
                            {(client.default_language || "de").toUpperCase()} ·
                            Zahlungsziel: {client.payment_terms_days} Tage
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openNewInvoice(client.id)}
                            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800"
                          >
                            Neue Rechnung
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditClient(client)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700"
                          >
                            Bearbeiten
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}

              {!loading && tab === "invoices" ? (
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Filter: Kunde">
                      <select
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Alle Kunden</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.company_name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Filter: Status">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Alle Status</option>
                        <option value="draft">Entwurf</option>
                        <option value="issued">Ausgestellt</option>
                        <option value="paid">Bezahlt</option>
                        <option value="cancelled">Storniert</option>
                      </select>
                    </Field>
                  </div>

                  {invoices.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
                      Noch keine B2B-Rechnungen. Klicke auf „+ Rechnung“, um
                      einen Entwurf anzulegen.
                    </div>
                  ) : (
                    invoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="rounded-xl border border-slate-200 bg-white p-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-slate-900">
                                {inv.invoice_no || inv.draft_code}
                              </span>
                              <span
                                className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusBadge(
                                  inv.status
                                )}`}
                              >
                                {STATUS_LABELS[inv.status] || inv.status}
                              </span>
                            </div>
                            <div className="mt-1 text-sm text-slate-700">
                              {inv.company_name}{" "}
                              <span className="text-slate-400">
                                ({inv.client_code})
                              </span>
                            </div>
                            <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                              <div>
                                Leistungszeitraum:{" "}
                                {formatDisplayDate(inv.service_period_from)} –{" "}
                                {formatDisplayDate(inv.service_period_to)}
                              </div>
                              <div>
                                Rechnungsdatum:{" "}
                                {formatDisplayDate(inv.invoice_date)} · Fällig:{" "}
                                {formatDisplayDate(inv.due_date)}
                              </div>
                              <div>
                                Betrag: {money(inv.gross_amount)} {inv.currency}{" "}
                                · Steuerfall: {inv.tax_mode}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                runAction(() =>
                                  openB2BInvoicePDF(
                                    inv.id,
                                    inv.status === "draft"
                                  )
                                )
                              }
                              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700"
                            >
                              {inv.status === "draft" ? "PDF Vorschau" : "PDF"}
                            </button>

                            {inv.status === "draft" ? (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => openEditInvoice(inv)}
                                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700"
                                >
                                  Bearbeiten
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    runAction(async () => {
                                      if (
                                        !window.confirm(
                                          "Rechnung wirklich ausstellen? Danach unveränderlich."
                                        )
                                      ) {
                                        return;
                                      }
                                      await issueB2BInvoice(inv.id);
                                    })
                                  }
                                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"
                                >
                                  Ausstellen
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    runAction(async () => {
                                      if (
                                        !window.confirm("Entwurf löschen?")
                                      ) {
                                        return;
                                      }
                                      await deleteB2BDraft(inv.id);
                                    })
                                  }
                                  className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700"
                                >
                                  Löschen
                                </button>
                              </>
                            ) : null}

                            {inv.status === "issued" ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  runAction(() => markB2BInvoicePaid(inv.id))
                                }
                                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800"
                              >
                                Als bezahlt markieren
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>

            {showClientForm ? (
              <div className="max-h-[48vh] overflow-y-auto border-t border-slate-100 bg-slate-50 px-5 py-4">
                <div className="mb-1 text-sm font-semibold text-slate-800">
                  {editingClientId ? "Kunde bearbeiten" : "Neuer B2B-Kunde"}
                </div>
                <p className="mb-3 text-xs text-slate-500">
                  Stammdaten für die Rechnung. Pflichtfelder sind mit * markiert.
                </p>

                <SectionTitle>Identität</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {!editingClientId ? (
                    <Field
                      label="Kunden-Code"
                      required
                      hint="Interner Kurzcode, z.B. blockpit"
                    >
                      <input
                        className={inputClass}
                        value={clientForm.clientCode}
                        onChange={(e) =>
                          setClientForm((p) => ({
                            ...p,
                            clientCode: e.target.value,
                          }))
                        }
                      />
                    </Field>
                  ) : null}
                  <Field label="Firmenname" required>
                    <input
                      className={inputClass}
                      value={clientForm.companyName}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          companyName: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Rechtsform" hint="z.B. AG, GmbH">
                    <input
                      className={inputClass}
                      value={clientForm.legalForm}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          legalForm: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>

                <SectionTitle>Adresse</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="Straße / Hausnummer" required className="lg:col-span-2">
                    <input
                      className={inputClass}
                      value={clientForm.street}
                      onChange={(e) =>
                        setClientForm((p) => ({ ...p, street: e.target.value }))
                      }
                    />
                  </Field>
                  <Field label="PLZ" required>
                    <input
                      className={inputClass}
                      value={clientForm.postalCode}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          postalCode: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Stadt" required>
                    <input
                      className={inputClass}
                      value={clientForm.city}
                      onChange={(e) =>
                        setClientForm((p) => ({ ...p, city: e.target.value }))
                      }
                    />
                  </Field>
                  <Field label="Ländercode" required hint="ISO-2, z.B. AT, DE">
                    <input
                      className={inputClass}
                      value={clientForm.countryCode}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          countryCode: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Ländername" required>
                    <input
                      className={inputClass}
                      value={clientForm.countryName}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          countryName: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>

                <SectionTitle>Steuer & Kontakt</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field
                    label="USt-IdNr. des Kunden"
                    hint="Für Reverse Charge erforderlich"
                  >
                    <input
                      className={inputClass}
                      value={clientForm.vatId}
                      onChange={(e) =>
                        setClientForm((p) => ({ ...p, vatId: e.target.value }))
                      }
                    />
                  </Field>
                  <Field label="Billing E-Mail">
                    <input
                      type="email"
                      className={inputClass}
                      value={clientForm.billingEmail}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          billingEmail: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Standardsprache der Rechnung">
                    <select
                      className={inputClass}
                      value={clientForm.defaultLanguage}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          defaultLanguage: e.target.value,
                        }))
                      }
                    >
                      <option value="de">Deutsch</option>
                      <option value="en">English</option>
                    </select>
                  </Field>
                  <Field
                    label="Zahlungsziel (Tage)"
                    hint="Wird für Fälligkeitsdatum vorgeschlagen"
                  >
                    <input
                      type="number"
                      className={inputClass}
                      value={clientForm.paymentTermsDays}
                      onChange={(e) =>
                        setClientForm((p) => ({
                          ...p,
                          paymentTermsDays: Number(e.target.value || 0),
                        }))
                      }
                    />
                  </Field>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={saveClient}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Kunde speichern
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowClientForm(false)}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : null}

            {showInvoiceForm ? (
              <div className="max-h-[52vh] overflow-y-auto border-t border-slate-100 bg-slate-50 px-5 py-4">
                <div className="mb-1 text-sm font-semibold text-slate-800">
                  {editingInvoiceId
                    ? "Entwurf bearbeiten"
                    : "Neue B2B-Rechnung (Entwurf)"}
                </div>
                <p className="mb-3 text-xs text-slate-500">
                  Noch keine echte Rechnungsnummer. Erst „Ausstellen“ vergibt
                  V3-B2B-… und macht die Rechnung unveränderlich.
                </p>

                <SectionTitle>Kunde & Steuer</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="Kunde" required>
                    <select
                      className={inputClass}
                      value={invoiceForm.clientId}
                      onChange={(e) => onClientChange(e.target.value)}
                    >
                      <option value="">Kunde wählen…</option>
                      {activeClients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.company_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Steuerfall" required>
                    <select
                      className={inputClass}
                      value={invoiceForm.taxMode}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          taxMode: e.target.value,
                        }))
                      }
                    >
                      {TAX_MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Rechnungssprache">
                    <select
                      className={inputClass}
                      value={invoiceForm.language}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          language: e.target.value,
                        }))
                      }
                    >
                      <option value="de">Deutsch</option>
                      <option value="en">English</option>
                    </select>
                  </Field>
                  {invoiceForm.taxMode === "DOMESTIC_STANDARD" ? (
                    <Field label="Umsatzsteuersatz (%)" required>
                      <input
                        type="number"
                        className={inputClass}
                        value={invoiceForm.vatRate}
                        onChange={(e) =>
                          setInvoiceForm((p) => ({
                            ...p,
                            vatRate: Number(e.target.value || 0),
                          }))
                        }
                      />
                    </Field>
                  ) : null}
                </div>

                <SectionTitle>Daten & Fristen</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Field
                    label="Rechnungsdatum"
                    required
                    hint="Ausstellungsdatum der Rechnung"
                  >
                    <input
                      type="date"
                      className={inputClass}
                      value={invoiceForm.invoiceDate}
                      onChange={(e) => onInvoiceDateChange(e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Fälligkeitsdatum"
                    hint="Wird aus Zahlungsziel vorausgefüllt"
                  >
                    <input
                      type="date"
                      className={inputClass}
                      value={invoiceForm.dueDate}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          dueDate: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Leistungszeitraum von" required>
                    <input
                      type="date"
                      className={inputClass}
                      value={invoiceForm.servicePeriodFrom}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          servicePeriodFrom: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Leistungszeitraum bis" required>
                    <input
                      type="date"
                      className={inputClass}
                      value={invoiceForm.servicePeriodTo}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          servicePeriodTo: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>

                <SectionTitle>Referenzen (optional)</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="PO / Bestellreferenz">
                    <input
                      className={inputClass}
                      value={invoiceForm.poNumber}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          poNumber: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="Vertragsreferenz">
                    <input
                      className={inputClass}
                      value={invoiceForm.contractReference}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          contractReference: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>

                <SectionTitle>Positionen</SectionTitle>
                <div className="mb-2 hidden gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:grid sm:grid-cols-[1fr_90px_130px_40px]">
                  <div>Leistungsbeschreibung</div>
                  <div>Menge</div>
                  <div>Einzelpreis (€)</div>
                  <div />
                </div>
                <div className="space-y-2">
                  {invoiceForm.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="grid gap-2 sm:grid-cols-[1fr_90px_130px_40px]"
                    >
                      <Field
                        label={idx === 0 ? "Leistungsbeschreibung" : null}
                        className="sm:[&>span]:hidden"
                      >
                        <input
                          className={inputClass}
                          placeholder="z.B. VeChain Accounting Data API – September 2026"
                          value={item.description}
                          onChange={(e) =>
                            updateItem(idx, { description: e.target.value })
                          }
                        />
                      </Field>
                      <Field
                        label={idx === 0 ? "Menge" : null}
                        className="sm:[&>span]:hidden"
                      >
                        <input
                          type="number"
                          className={inputClass}
                          value={item.qty}
                          onChange={(e) =>
                            updateItem(idx, {
                              qty: Number(e.target.value || 0),
                            })
                          }
                        />
                      </Field>
                      <Field
                        label={idx === 0 ? "Einzelpreis (€)" : null}
                        className="sm:[&>span]:hidden"
                      >
                        <input
                          type="number"
                          step="0.01"
                          className={inputClass}
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateItem(idx, {
                              unitPrice: Number(e.target.value || 0),
                            })
                          }
                        />
                      </Field>
                      <div className="flex items-end">
                        <button
                          type="button"
                          title="Position entfernen"
                          className="flex h-[38px] w-full items-center justify-center rounded-lg border border-slate-200 text-sm text-slate-600"
                          onClick={() =>
                            setInvoiceForm((p) => ({
                              ...p,
                              items: p.items.filter((_, i) => i !== idx),
                            }))
                          }
                        >
                          −
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    className="text-sm font-medium text-emerald-700"
                    onClick={() =>
                      setInvoiceForm((p) => ({
                        ...p,
                        items: [
                          ...p.items,
                          { description: "", qty: 1, unitPrice: 0 },
                        ],
                      }))
                    }
                  >
                    + Position hinzufügen
                  </button>
                  <div className="text-sm font-semibold text-slate-800">
                    Zwischensumme: {money(draftNetTotal)} €
                  </div>
                </div>

                <SectionTitle>Hinweise</SectionTitle>
                <Field label="Notiz auf der Rechnung">
                  <textarea
                    className={inputClass}
                    rows={2}
                    value={invoiceForm.notes}
                    onChange={(e) =>
                      setInvoiceForm((p) => ({ ...p, notes: e.target.value }))
                    }
                  />
                </Field>

                {(invoiceForm.taxMode === "CUSTOM" ||
                  invoiceForm.taxMode === "NON_EU_B2B") && (
                  <Field
                    label="Steuerhinweis (Override)"
                    className="mt-3"
                    hint="Eigener Text für den Steuerblock auf der PDF"
                  >
                    <textarea
                      className={inputClass}
                      rows={2}
                      value={invoiceForm.taxNoteOverride}
                      onChange={(e) =>
                        setInvoiceForm((p) => ({
                          ...p,
                          taxNoteOverride: e.target.value,
                        }))
                      }
                    />
                  </Field>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={saveInvoice}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Entwurf speichern
                  </button>
                  {editingInvoiceId ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        runAction(() =>
                          openB2BInvoicePDF(editingInvoiceId, true)
                        )
                      }
                      className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                    >
                      PDF Vorschau
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setShowInvoiceForm(false);
                      setEditingInvoiceId(null);
                    }}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
